/**
 * Dashboard — главная страница админки.
 * KPI-карточки + график доходов 30д + таблица нод + свежие платежи.
 */
import { useCustom } from "@refinedev/core";
import { Card, Col, Row, Statistic, Table, Tag, Typography, Space } from "antd";
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { API_URL } from "../dataProvider";
import { TestInvoiceButton } from "../components/TestInvoiceButton";

interface DashboardData {
  active_subs: { total: number; by_tariff: Record<string, number> };
  users: { total: number; with_active_subs: number };
  revenue: { total_rub: number; rub_30d: number; payments_30d: number };
  daily_revenue: { day: string; revenue: number; payments: number }[];
  online_now: number;
  nodes: {
    id: number; name: string; ip: string; location: string;
    capacity_basic: number; capacity_parental: number; capacity_premium: number;
    active_subs: number;
  }[];
  recent_payments: {
    id: number; user_id: number; username: string | null; first_name: string | null;
    amount_rub: number; gateway: string; paid_at: string | null;
  }[];
}

const fmtRub = (n: number) =>
  new Intl.NumberFormat("ru-RU").format(n) + " ₽";

export const DashboardPage = () => {
  const { data, isLoading } = useCustom<DashboardData>({
    url: `${API_URL}/admin/dashboard`, method: "get",
  });
  const d = data?.data;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <TestInvoiceButton />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Доход за 30 дней"
                       value={d?.revenue.rub_30d ?? 0}
                       formatter={(v) => fmtRub(Number(v))} />
            <Typography.Text type="secondary">
              {d?.revenue.payments_30d ?? 0} платежей
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Активные подписки"
                       value={d?.active_subs.total ?? 0} />
            <Typography.Text type="secondary">
              у {d?.users.with_active_subs ?? 0} юзеров
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Online сейчас"
                       value={d?.online_now ?? 0}
                       suffix={<Typography.Text type="secondary"> подп.</Typography.Text>} />
            <Typography.Text type="secondary">
              из {d?.active_subs.total ?? 0} активных
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card loading={isLoading}>
            <Statistic title="Юзеров всего"
                       value={d?.users.total ?? 0} />
            <Typography.Text type="secondary">
              {Object.entries(d?.active_subs.by_tariff || {}).map(([k, v]) =>
                `${k}: ${v}`).join(" · ") || "нет активных"}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Card title="Доход по дням (30д)" loading={isLoading}>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <AreaChart data={d?.daily_revenue || []}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1677ff" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtRub(Number(v))} />
              <Area type="monotone" dataKey="revenue"
                    stroke="#1677ff" fill="url(#rev)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Ноды" loading={isLoading}>
            <Table size="small" pagination={false} rowKey="id"
                   dataSource={d?.nodes || []}
                   columns={[
                     { title: "Имя", dataIndex: "name" },
                     { title: "IP", dataIndex: "ip" },
                     { title: "Локация", dataIndex: "location" },
                     { title: "Тарифы", render: (_, n) => (
                       <Space size={4}>
                         <Tag color="blue">basic {n.capacity_basic}</Tag>
                         <Tag color="purple">parental {n.capacity_parental}</Tag>
                         <Tag color="gold">premium {n.capacity_premium}</Tag>
                       </Space>
                     )},
                     { title: "Активные", dataIndex: "active_subs", align: "right" },
                   ]} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="Свежие платежи" loading={isLoading}>
            <Table size="small" pagination={false} rowKey="id"
                   dataSource={d?.recent_payments || []}
                   columns={[
                     { title: "Юзер", render: (_, p) =>
                       p.username || p.first_name || `tg:${p.user_id}` },
                     { title: "Сумма", dataIndex: "amount_rub",
                       align: "right", render: (v: number) => fmtRub(v) },
                     { title: "Когда", dataIndex: "paid_at",
                       render: (v: string) => v ? new Date(v).toLocaleString("ru-RU") : "—" },
                   ]} />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};
